import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingShell from "@/components/MarketingShell";
import { PHOTOS } from "@/lib/photos";

const BOOK_LINK = "/contact?type=Community%20Event%20Booking&topic=Jazz%20Meets%20Jewish";

const TARGETS = [
  "Synagogues",
  "Chabad houses",
  "Jewish federations",
  "Hillel chapters",
  "JCCs & cultural centers",
  "Day schools & yeshivas",
  "Donor & lay-leader events",
  "Shabbatons & scholar-in-residence weekends",
];

const ACTS = [
  {
    eyebrow: "Act I",
    title: "Where I came from.",
    body: "Reform kid, LA, a piano. Jazz arrives as something closer to a spiritual calling than a career — and then 9/11 happens, and the question of what the music is actually for stops being abstract.",
  },
  {
    eyebrow: "Act II",
    title: "How I got here.",
    body: "Princeton, then two years learning in Eretz Yisrael. A yellow jacket at Selichos in 770. Keeping Shabbos meant walking away from jazz almost entirely, because Dan couldn't picture showing up in those rooms as an observant Jew. Years later he hears niggunim floating around Las Vegas, played by people who aren't Jewish at all.",
    highlight: true,
  },
  {
    eyebrow: "Act III",
    title: "What the story means.",
    body: "Possibility, Geulah, creativity, improvisation. Everyone has access to people, places and rooms nobody else can reach. The tension between Jewish life and your own individuality isn't the problem — it's where your deepest contribution starts.",
  },
];

const FAQ = [
  {
    q: "How long is the evening?",
    a: "Seventy-five minutes of concert and storytelling, with a meet-the-artist schmooze built in straight afterwards. We can shape the run time around your program.",
  },
  {
    q: "Is this a concert or a talk?",
    a: "Both, and neither. Many speakers come with slides — Dan comes with a piano. The stories are punctuated by live jazz, niggunim and improvisation, and the room's reactions change where it goes, so no two evenings unfold the same way.",
  },
  {
    q: "What kind of room does this work in?",
    a: "Sanctuaries, social halls, salons, atriums, campus lounges. Anywhere with a piano (or where we can bring one) and an audience of 30–500. The format scales gracefully up and down.",
  },
  {
    q: "Do you provide the piano and sound?",
    a: "Dan plays whatever you have, and if you don't have one we'll coordinate a rental together. Sound reinforcement can be arranged when needed — the requirements are simple, and Dan will walk your team through them.",
  },
  {
    q: "How do we book?",
    a: "Use the button on this page or write Dan directly. Plan on 6–12 weeks of lead time for the best calendar windows; travel is worked out per event.",
  },
];

export default function InteractiveConcerts() {
  return (
    <MarketingShell active="/book">
      {/* Hero */}
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="grid md:grid-cols-[1.25fr_1fr] gap-8 lg:gap-12 items-center"
      >
        <div>
          <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
            Book a concert
          </p>
          <h1
            className="leading-[0.88]"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 500,
              fontSize: "clamp(52px, 7.4vw, 118px)",
              textWrap: "balance",
              maxWidth: "14ch",
            }}
            data-testid="book-hero-title"
          >
            Jazz Meets <em style={{ color: "#F2D589" }}>Jewish</em>
          </h1>
          <p
            className="mt-4 text-xl sm:text-2xl leading-snug"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "rgba(245,239,227,0.9)", fontStyle: "italic" }}
            data-testid="book-hero-subtitle"
          >
            An Unforgettable Night of Song and Inspiration
          </p>
          <p
            className="mt-7 text-lg sm:text-xl leading-snug"
            style={{ color: "#F2D589", fontFamily: "'Cormorant Garamond', serif" }}
          >
            Many speakers come with slides. I come with a piano.
          </p>
          <p
            className="mt-4 max-w-[40rem] text-base sm:text-lg leading-relaxed"
            style={{ color: "rgba(245,239,227,0.82)" }}
            data-testid="book-hero-blurb"
          >
            A 75-minute interactive concert and storytelling experience: live
            jazz, niggunim, personal stories, humour and improvisation woven
            into an exploration of Jewish identity, creativity, Geulah, and the
            unique light each person can bring into the places only they can
            reach.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to={BOOK_LINK}
              data-testid="book-hero-cta"
              className="inline-flex items-center justify-center rounded-full px-6 h-12 text-sm uppercase tracking-[0.22em]"
              style={{ backgroundColor: "transparent", color: "#F2D589", border: "1px solid #F2D589", fontWeight: 600 }}
            >
              Book Dan for your community &rsaquo;
            </Link>
            <a
              href="#format"
              className="inline-flex items-center justify-center rounded-full px-6 h-12 text-sm uppercase tracking-[0.22em] border border-white/25 hover:bg-white/10 transition-colors"
            >
              See the three acts
            </a>
          </div>
        </div>
        <div
          className="rounded-2xl overflow-hidden border"
          style={{ borderColor: "rgba(255,255,255,0.12)", aspectRatio: "4 / 5" }}
          data-testid="book-hero-photo"
        >
          <img
            src={PHOTOS.danPortrait}
            alt="Dan Berry at the piano"
            className="w-full h-full object-cover"
          />
        </div>
      </motion.section>

      {/* The story */}
      <section className="mt-20 grid md:grid-cols-[1fr_1.2fr] gap-8 items-start" data-testid="book-story">
        <div>
          <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
            The evening
          </p>
          <h2
            className="text-3xl sm:text-5xl leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, textWrap: "balance" }}
          >
            From Reform to Chassidic, with jazz the whole way through.
          </h2>
        </div>
        <div className="space-y-4 text-base sm:text-lg leading-relaxed" style={{ color: "rgba(245,239,227,0.82)" }}>
          <p>
            When Dan Berry left LA to study music at Princeton, the last thing
            he expected was for jazz to become part of a much bigger journey
            from Reform to Chassidic Jew.
          </p>
          <p>
            From discovering a spiritual calling in music after 9/11, to years
            of learning in Eretz Yisrael, to the challenge of figuring out how
            to remain fully observant without walking away from the musical
            world he loved, Dan&apos;s path eventually led him to a new
            question:
          </p>
          <p className="text-xl sm:text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#F2D589", fontStyle: "italic" }}>
            What if the tension between Jewish life and your individuality is
            where your deepest contribution begins?
          </p>
          <p>
            Unlike a typical speaker with slides, Dan comes with a piano.
            We&apos;re just going to flow with it and see what happens — which
            is why no two evenings unfold quite the same way.
          </p>
        </div>
      </section>

      {/* Three acts */}
      <section id="format" className="mt-20">
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          The arc of the night
        </p>
        <h2 className="text-3xl sm:text-5xl leading-tight mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Seventy-five minutes. Three acts.
        </h2>
        <ol className="space-y-5" data-testid="book-acts">
          {ACTS.map((step) => (
            <li
              key={step.eyebrow}
              className="rounded-2xl border p-6 sm:p-7 backdrop-blur-md"
              style={{
                borderColor: step.highlight ? "rgba(232,196,138,0.4)" : "rgba(255,255,255,0.12)",
                backgroundColor: step.highlight ? "rgba(232,196,138,0.06)" : "rgba(8,4,25,0.4)",
              }}
            >
              <p className="text-[10px] uppercase tracking-[0.32em] mb-2" style={{ color: "#F2D589" }}>
                {step.eyebrow}
              </p>
              <h3 className="text-2xl sm:text-3xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {step.title}
              </h3>
              <p className="text-base sm:text-lg leading-relaxed" style={{ color: "rgba(245,239,227,0.82)" }}>
                {step.body}
              </p>
            </li>
          ))}
        </ol>
        <p className="mt-6 text-base leading-relaxed max-w-[48rem]" style={{ color: "rgba(245,239,227,0.7)" }}>
          The music carries the story rather than decorating it — a standard
          like <em>East of the Sun</em> for Princeton, a niggun for Eretz
          Yisrael, improvisation for everything still unresolved.
        </p>
      </section>

      {/* Then the real part */}
      <section className="mt-20 grid md:grid-cols-[1fr_1.15fr] gap-8 items-start" data-testid="book-shape">
        <div>
          <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
            How it runs
          </p>
          <h2 className="text-3xl sm:text-4xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Seventy-five minutes, then the real part.
          </h2>
        </div>
        <div className="space-y-4 text-base leading-relaxed" style={{ color: "rgba(245,239,227,0.8)" }}>
          <p>
            The concert runs about 75 minutes, with a meet-the-artist schmooze
            built in straight afterwards. Dan stays — farbrenging, answering the
            questions people were too shy to ask in front of a crowd. That&apos;s
            where the night stops being a performance and becomes a connection.
          </p>
          <p>
            If someone in the room is wrestling with how an observant life and
            their own creative work fit together, this is the part of the evening
            they&apos;ll remember. Dan&apos;s invitation from the stage is
            simple: come find me when we wrap here.
          </p>
        </div>
      </section>

      {/* Who books this */}
      <section className="mt-20">
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          Built for
        </p>
        <h2 className="text-3xl sm:text-5xl leading-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Jewish communities who want more than a recital.
        </h2>
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {TARGETS.map((t) => (
            <li
              key={t}
              className="rounded-xl border px-4 py-3 text-base"
              style={{
                borderColor: "rgba(255,255,255,0.12)",
                backgroundColor: "rgba(8,4,25,0.35)",
                color: "rgba(245,239,227,0.86)",
              }}
            >
              {t}
            </li>
          ))}
        </ul>
        <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-[48rem]" style={{ color: "rgba(245,239,227,0.78)" }}>
          It&apos;s just as comfortable opening a federation campaign event,
          anchoring a synagogue&apos;s arts series, or closing a Chabad-house
          retreat. Communities book it when they want their members inspired
          rather than lectured.
        </p>
      </section>

      {/* FAQ */}
      <section className="mt-20">
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          Practical
        </p>
        <h2 className="text-3xl sm:text-5xl leading-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          The things you&apos;re about to ask.
        </h2>
        <dl className="space-y-5" data-testid="book-faq">
          {FAQ.map((f) => (
            <div
              key={f.q}
              className="rounded-2xl border p-6 backdrop-blur-md"
              style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,25,0.35)" }}
            >
              <dt className="text-lg sm:text-xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: "#F2D589" }}>
                {f.q}
              </dt>
              <dd className="text-base leading-relaxed" style={{ color: "rgba(245,239,227,0.82)" }}>
                {f.a}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Bottom CTA */}
      <section
        className="mt-20 rounded-2xl border p-8 sm:p-10 backdrop-blur-md text-center"
        style={{ borderColor: "rgba(232,196,138,0.3)", backgroundColor: "rgba(8,4,25,0.5)" }}
      >
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          Ready when you are
        </p>
        <h2
          className="text-3xl sm:text-5xl leading-tight mb-3"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
        >
          Let&apos;s give your community a night they keep.
        </h2>
        <p className="max-w-[40rem] mx-auto text-base sm:text-lg leading-relaxed mb-6" style={{ color: "rgba(245,239,227,0.78)" }}>
          Tell Dan a little about your community, the date you&apos;re eyeing,
          and what you&apos;re hoping the night feels like. He writes back
          personally.
        </p>
        <Link
          to={BOOK_LINK}
          data-testid="book-bottom-cta"
          className="inline-flex items-center justify-center rounded-full px-7 h-12 text-sm uppercase tracking-[0.22em]"
          style={{ backgroundColor: "transparent", color: "#F2D589", border: "1px solid #F2D589", fontWeight: 600 }}
        >
          Start the conversation &rsaquo;
        </Link>
      </section>
    </MarketingShell>
  );
}
